'use client'

import { motion } from 'motion/react'
import { Fragment, forwardRef, useCallback, useMemo, useState } from 'react'
import { IMenuGroup } from '@/api/restaurants/types/restaurant-types'
import { Button } from '@/components/button'
import { RestaurantDetailMenuItem } from '@/features/restaurants/restaurant-detail/restaurant-detail-menu/restaurant-detail-menu-item'
import { SvgOutlinedChevronRight } from '@/icons/svg-outlined-chevron-right'
import { cn } from '@/utils/styles/cn'
import { COLORS } from '@/utils/styles/colors'

interface IRestaurantDetailMenuGroupProps {
    menuGroup: IMenuGroup
    initialIsCollapsed: boolean
    currencySymbol: string
}

export const RestaurantDetailMenuGroup = forwardRef<
    HTMLDivElement,
    IRestaurantDetailMenuGroupProps
>(({ menuGroup, initialIsCollapsed, currencySymbol }, ref) => {
    const [isCollapsed, setIsCollapsed] = useState(initialIsCollapsed ?? false)

    const toggleCollapse = useCallback(() => {
        setIsCollapsed((prev) => !prev)
    }, [])

    const nameLength = menuGroup.name.length

    const hasMenuItems = useMemo(() => Boolean(menuGroup.menuItems?.length), [menuGroup.menuItems])

    return (
        <div ref={ref} className="flex flex-col p-4 md:p-6 rounded-l bg-bg-overlay-black-subtle">
            <div
                className={cn(
                    'flex flex-row justify-between w-full items-center',
                    hasMenuItems && 'mb-5'
                )}
            >
                <h5
                    className={cn(
                        'font-bold overflow-hidden text-ellipsis w-fit max-w-[90%]',
                        nameLength < 15
                            ? 'text-h4'
                            : nameLength < 20
                              ? 'text-p-lg md:text-h4'
                              : nameLength < 30
                                ? 'md:text-h4 text-h5'
                                : 'md:text-p-lg text-p-sm'
                    )}
                >
                    {menuGroup.name}
                </h5>

                {Boolean(menuGroup.menuItems?.length) && (
                    <Fragment>
                        <div className="hidden md:block min-h-[1px] max-h-[1px] bg-bg-neutral-surface-strong flex-1 max-w-full ml-10 mr-7" />

                        <Button
                            styleType="invisible"
                            shape="circle"
                            onClick={toggleCollapse}
                            // Elevate over the fade overlay which is on the menu when there are multiple categories and they are initially collapsed
                            className="z-[1]"
                        >
                            <motion.div
                                initial={{ rotate: isCollapsed ? 90 : -90 }}
                                animate={{ rotate: isCollapsed ? 90 : -90 }}
                            >
                                <SvgOutlinedChevronRight color={COLORS.fg.primary} size={24} />
                            </motion.div>
                        </Button>
                    </Fragment>
                )}
            </div>

            <motion.div
                className="overflow-hidden"
                initial={{ height: isCollapsed ? 0 : 'auto' }}
                animate={{ height: isCollapsed ? 0 : 'auto' }}
                transition={{ type: 'spring', duration: 0.4, bounce: 0.08 }}
            >
                <motion.div
                    initial={{ opacity: isCollapsed ? 0 : 1 }}
                    animate={{ opacity: isCollapsed ? 0 : 1 }}
                    transition={{ duration: 0.3 }}
                    className="grid grid-cols-1 md:grid-cols-3 gap-x-4 md:gap-x-20 gap-y-6"
                >
                    {menuGroup.menuItems?.map((menuItem) => {
                        return (
                            <RestaurantDetailMenuItem
                                currencySymbol={currencySymbol}
                                key={
                                    'restaurant-detail-menu-group' + menuGroup.uuid + menuItem.uuid
                                }
                                menuItem={menuItem}
                            />
                        )
                    })}
                </motion.div>
            </motion.div>
        </div>
    )
})

RestaurantDetailMenuGroup.displayName = 'RestaurantDetailMenuGroup'
